import { useEffect, useState } from 'react';
import useGameStore, { valuationLiveOf } from '../store/gameStore';
import { money } from '../lib/format';
import { INVESTMENT_TIER_META } from '../types/gameTypes';
import type { InvestmentTier } from '../types/gameTypes';

const TIERS: InvestmentTier[] = ['SAVINGS', 'REIT', 'VENTURE'];

/** Liquidity-squeeze settlement: manual share transfer, auto-resolve, or fire-sale. */
const EmergencyModal = () => {
  const store = useGameStore();
  const emergency = store.emergency;
  const [picks, setPicks] = useState<Record<string, number>>({});

  useEffect(() => setPicks({}), [emergency?.debtorId]);
  if (!emergency) return null;
  const debtor = store.players.find((p) => p.id === emergency.debtorId);
  if (!debtor) return null;

  const holdings = store.board
    .map((tile) => ({ tile, owned: tile.shares[emergency.debtorId] ?? 0 }))
    .filter((h) => h.owned > 0)
    .sort((a, b) => (emergency.tileId === a.tile.id ? -1 : emergency.tileId === b.tile.id ? 1 : 0));

  const pickedValue = Object.entries(picks).reduce((sum, [tileId, shares]) => {
    const tile = store.board.find((t) => t.id === tileId);
    if (!tile) return sum;
    return sum + shares * (valuationLiveOf(tile) / Math.max(1, tile.totalShares));
  }, 0);
  const deficit = Math.max(0, emergency.amountDue - emergency.cashAvailable);
  // Live deficit (recomputed after borrow/withdraw) + financing options.
  const liveCash = Math.max(0, debtor.cash);
  const liveDeficit = Math.max(0, emergency.amountDue - liveCash);
  const headroom = Math.max(0, store.debtCeilingOf(debtor.id) - debtor.debt - store.loanExposureOf(debtor.id));
  const canBorrow = headroom >= liveDeficit;
  const canPayCash = debtor.cash >= emergency.amountDue;

  const resolveManual = () => {
    const allocations = Object.entries(picks)
      .filter(([, shares]) => shares > 0)
      .map(([tileId, shares]) => ({ tileId, shares, creditorId: emergency.creditorShares[0]?.playerId ?? '' }));
    store.emergencySwap(allocations);
    const swapped = Object.entries(picks).reduce((sum, [tileId, shares]) => {
      const tile = store.board.find((t) => t.id === tileId);
      return tile ? sum + shares * (valuationLiveOf(tile) / tile.totalShares) : sum;
    }, 0);
    const stillShort = deficit - swapped;
    if (stillShort > 0 && debtor.cash < stillShort) store.emergencyFireSale();
    store.resumeAfterEmergency();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-rose-500/60 bg-slate-900 shadow-2xl">
        <div className="bg-rose-600/90 px-5 py-3">
          <p className="text-sm font-extrabold tracking-wide text-white">Liquidity Squeeze: Cash Deficit of {money(deficit)}</p>
          <p className="text-[11px] text-rose-100">{emergency.debtorName} owes {money(emergency.amountDue)} ({emergency.label}) · cash {money(emergency.cashAvailable)}</p>
        </div>
        <div className="max-h-[75vh] space-y-3 overflow-auto p-5">
          <p className="rounded-lg border border-rose-400/40 bg-rose-950/30 px-3 py-1.5 text-[11px] text-slate-300">
            Požadováno <b className="text-rose-300">{money(emergency.amountDue)}</b> · hotovost <b className="text-slate-100">{money(liveCash)}</b> ·
            deficit <b className={liveDeficit > 0 ? 'text-rose-300' : 'text-emerald-300'}>{money(liveDeficit)}</b>
          </p>
          {/* ---- Option A: credit line --------------------------------------------- */}
          <div className={`rounded-xl border p-3 ${canBorrow ? 'border-amber-400/50 bg-amber-500/5' : 'border-slate-700/60 bg-slate-950/60 opacity-50'}`}>
            <p className="text-xs font-bold uppercase tracking-widest text-amber-300">A · Půjčka / čerpání úvěrové linky</p>
            <p className="mt-1 text-[11px] text-slate-400">
              Úvěrový strop: {money(headroom)} {canBorrow ? '· půjčka pokryje deficit' : '· nedostatečný na krytí deficitu'}
            </p>
            <button
              type="button"
              disabled={!canBorrow || liveDeficit <= 0}
              onClick={() => store.emergencyBorrow()}
              className="mt-2 w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-xs font-extrabold text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500"
            >
              Čerpat půjčku +{money(liveDeficit)}
            </button>
          </div>

          {/* ---- Option B: treasury withdrawals ------------------------------------ */}
          <div className="rounded-xl border border-sky-400/50 bg-sky-500/5 p-3">
            <p className="text-xs font-bold uppercase tracking-widest text-sky-300">B · Výběr z investic</p>
            {TIERS.every((tier) => !store.investmentOf(debtor.id, tier)) ? (
              <p className="mt-1 text-[11px] text-slate-500">Žádné prostředky v pokladních fondech.</p>
            ) : (
              TIERS.map((tier) => {
                const entry = store.investmentOf(debtor.id, tier);
                if (!entry) return null;
                return (
                  <div key={tier} className="mt-2 flex items-center justify-between gap-2 text-xs">
                    <span className="text-slate-200">
                      {INVESTMENT_TIER_META[tier].name} · {money(entry.value)}
                    </span>
                    <button
                      type="button"
                      disabled={liveDeficit <= 0}
                      onClick={() => store.emergencyWithdraw(tier)}
                      className="rounded-lg border border-sky-400/70 bg-sky-600/80 px-3 py-1 font-bold text-white hover:bg-sky-500 disabled:border-slate-700 disabled:bg-slate-800 disabled:text-slate-500"
                    >
                      Vybrat (kryje deficit {money(liveDeficit)})
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* ---- CTA: pay in cash once the deficit is covered ----------------------- */}
          <button
            type="button"
            disabled={!canPayCash}
            onClick={() => store.emergencyPayCash()}
            className="w-full rounded-xl bg-gradient-to-r from-emerald-400 to-teal-300 px-4 py-2.5 text-sm font-extrabold text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500"
          >
            Zaplatit hotovostí {money(emergency.amountDue)} a pokračovat
          </button>

          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Nebo řeš akciemi (swap / likvidace)</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Pick shares to transfer to creditors (fair live value, no discount)</p>
          <div className="mt-2 max-h-56 space-y-1.5 overflow-auto pr-1">
            {holdings.map(({ tile, owned }) => {
              const picked = picks[tile.id] ?? 0;
              const unit = valuationLiveOf(tile) / Math.max(1, tile.totalShares);
              return (
                <div key={tile.id} className={`rounded-lg border p-2 text-xs ${emergency.tileId === tile.id ? 'border-amber-400/60 bg-amber-500/5' : 'border-slate-700/60 bg-slate-950/50'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-100">{tile.name}{emergency.tileId === tile.id ? ' · origin' : ''}</span>
                    <span className="text-slate-400">{owned} sh · {money(unit)}/sh</span>
                  </div>
                  <input type="range" min={0} max={owned} value={picked} onChange={(e) => setPicks((p) => ({ ...p, [tile.id]: Number(e.target.value) }))} className="mt-1 w-full accent-rose-400" />
                  <p className="text-[11px] text-slate-400">Transfer <b className="text-rose-200">{picked}</b> sh ≈ {money(picked * unit)}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-slate-300">Selected value: <b className={pickedValue >= deficit ? 'text-emerald-300' : 'text-rose-300'}>{money(pickedValue)}</b> / {money(deficit)} needed</p>
          <div className="mt-4 space-y-2">
            <button onClick={() => { store.autoResolveEmergency(); store.resumeAfterEmergency(); }} className="w-full rounded-xl bg-gradient-to-r from-emerald-400 to-teal-300 px-4 py-2.5 text-sm font-extrabold text-slate-950">Auto-Resolve Best Value (swap → fire-sale)</button>
            <button disabled={pickedValue <= 0} onClick={resolveManual} className="w-full rounded-xl bg-gradient-to-r from-rose-400 to-orange-400 px-4 py-2.5 text-sm font-extrabold text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500">Transfer Selected Shares to Creditor</button>
            <button onClick={() => { store.emergencyFireSale(); store.resumeAfterEmergency(); }} className="w-full rounded-xl bg-slate-800 px-4 py-2.5 text-xs font-semibold text-slate-300">Emergency Fire-Sale (10% distress discount)</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyModal;
